// import components
import Clusters from './Clusters/Clusters';
import FormationAndSortorder from './FormationAndSortorder/FormationAndSortorder';
import PlanUnitDetails from './components/PlanUnitDetails';
import { CostCalculationExternalSettings } from 'components/shared/CostCalculationExternalSurcharge/CostCalculationExternalSettings';
import DutyCodesGrid from 'components/shared/DutyCodesGrid/DutyCodesGrid';
import PlanUnitSelector from 'components/shared/PlanUnitSelector';
import { getDefaultInterval } from 'components/shared/PlanUnitSelector/util';
import Preferences from 'components/roster-portal/plan-units/Preferences/Preferences';
import Settings from 'components/roster-portal/plan-units/Settings/Settings';
import StyledTabs from 'components/shared/StyledTabs';
import TabPanel from 'components/shared/TabPanel';
import { styled, Tab, Tabs } from '@mui/material';

// import constants
import { StyledBoxFlexAlignCenterSpaceBetween } from 'components/shared/Box/Box.styles';
import { StyledStickyPaper } from 'components/shared/Paper/StyledStickyPaper';
import {
  StyledBottomBox,
  StyledDatePickerWrapper,
  StyledPlanUnitSelectorBox,
  StyledPlanUnitsHomeBox,
  StyledTopBox
} from './PlanUnitsHome.styles';

// import external libraries
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import hooks
import { useSelector } from 'hooks';
import useSubNavigation from 'hooks/useSubNavigation';

// import types
import type { PlanUnitMix } from 'components/types';
import { DatePicker } from 'components/shared/index';
import { TabType } from './PlanUnitsHome.enums';

const StyledTabPanel = styled(TabPanel)`
  min-height: 100%;
`;

const PlanUnitsHome = () => {
  const { t } = useTranslation('common', { keyPrefix: 'planUnitHome' });

  const {
    hasCostCenterCardPreferences,
    hasCostCenterCardEditPreferences,
    hasClustersOrgGrant,
    hasCostCenterCardCostCalculationExternalsSettingsView
  } = useSelector((state) => state.grants);

  const [subPage, setSubPage] = useState<TabType>(TabType.PLAN_UNIT_DETAILS);
  const { setPage } = useSubNavigation<TabType>(
    TabType.PLAN_UNIT_DETAILS,
    'page',
    setSubPage
  );

  const [isEditMode, setIsEditMode] = useState(false);

  const onTabChanged = (_, newValue: TabType) => {
    if (!isEditMode) {
      setPage(newValue, undefined, true);
    }
  };

  const [date, setDate] = useState<Date | null>(new Date());

  const [interval, setInterval] = useState(getDefaultInterval());
  useEffect(() => {
    setInterval({
      startDate: dayjs(date).startOf('month').toDate(),
      endDate: dayjs(date).endOf('month').toDate()
    });
  }, [date]);

  const [planUnitId, setPlanUnitId] = useState<number | undefined>(undefined);
  const onSelectPlanUnit = (options: PlanUnitMix[]) => {
    const selectedPlanUnits = options.flatMap((o) =>
      'costCenters' in o ? o.costCenters : o
    );

    if (selectedPlanUnits.length > 0) {
      const planUnit = selectedPlanUnits[0];
      setShowCostCalculationTab(
        hasCostCenterCardCostCalculationExternalsSettingsView &&
          (planUnit.type == 'agn' || planUnit.type == 'rgp')
      );

      setPlanUnitId(planUnit.id);
    } else {
      setPlanUnitId(undefined);
    }
  };

  const [showCostCalculationsTab, setShowCostCalculationTab] = useState(
    hasCostCenterCardCostCalculationExternalsSettingsView
  );
  useEffect(() => {
    if (
      !showCostCalculationsTab &&
      subPage == TabType.COST_CALCULATION_SETTINGS
    ) {
      setPage(TabType.PLAN_UNIT_DETAILS);
    }
  }, [showCostCalculationsTab]);

  return (
    <StyledPlanUnitsHomeBox>
      <StyledPlanUnitSelectorBox>
        <PlanUnitSelector
          isSingleSelect
          showNavigationCnt
          onSelected={onSelectPlanUnit}
          interval={interval}
          hideGroups
          disabled={isEditMode}
        />

        <StyledDatePickerWrapper>
          <DatePicker date={date} onChange={setDate} disabled={isEditMode} />
        </StyledDatePickerWrapper>
      </StyledPlanUnitSelectorBox>

      <StyledTopBox>
        <StyledTabs>
          <StyledStickyPaper sx={{ mx: 0 }} square>
            <StyledBoxFlexAlignCenterSpaceBetween>
              <Tabs
                value={subPage}
                indicatorColor="primary"
                textColor="primary"
                onChange={onTabChanged}
                className="tabs"
              >
                <Tab
                  label={t('tabs.planUnitDetails')}
                  value={TabType.PLAN_UNIT_DETAILS}
                  disabled={isEditMode}
                />

                {(hasCostCenterCardPreferences ||
                  hasCostCenterCardEditPreferences) && (
                  <Tab
                    label={t('tabs.dutyCodes')}
                    value={TabType.DUTY_CODES}
                    disabled={isEditMode}
                  />
                )}

                {(hasCostCenterCardPreferences ||
                  hasCostCenterCardEditPreferences) && (
                  <Tab
                    label={t('tabs.preferences')}
                    value={TabType.PREFERENCES}
                    disabled={isEditMode}
                  />
                )}

                <Tab
                  label={t('tabs.settings')}
                  value={TabType.SETTINGS}
                  disabled={isEditMode}
                />

                {hasCostCenterCardEditPreferences && (
                  <Tab
                    label={t('tabs.formationAndSortOrder')}
                    value={TabType.FORMATION_AND_SORT_ORDER}
                    disabled={isEditMode}
                  />
                )}

                {hasClustersOrgGrant && (
                  <Tab
                    label={t('tabs.clusters')}
                    value={TabType.CLUSTERS}
                    disabled={isEditMode}
                  />
                )}

                {showCostCalculationsTab && (
                  <Tab
                    label={t('tabs.costCalculationSettings')}
                    value={TabType.COST_CALCULATION_SETTINGS}
                    disabled={isEditMode}
                  />
                )}
              </Tabs>
            </StyledBoxFlexAlignCenterSpaceBetween>
          </StyledStickyPaper>
        </StyledTabs>
      </StyledTopBox>

      <StyledBottomBox>
        <StyledTabPanel
          value={TabType.PLAN_UNIT_DETAILS}
          activePage={subPage}
          title={t('tabs.planUnitDetails')}
        >
          {planUnitId && (
            <PlanUnitDetails
              planUnitId={planUnitId}
              referenceDate={date}
              isEditMode={isEditMode}
              setIsEditMode={setIsEditMode}
            ></PlanUnitDetails>
          )}
        </StyledTabPanel>

        {(hasCostCenterCardPreferences || hasCostCenterCardEditPreferences) && (
          <StyledTabPanel
            value={TabType.DUTY_CODES}
            activePage={subPage}
            title={t('tabs.dutyCodes')}
          >
            {planUnitId && (
              <DutyCodesGrid
                organizationalUnitId={planUnitId}
                referenceDate={date}
              ></DutyCodesGrid>
            )}
          </StyledTabPanel>
        )}

        {(hasCostCenterCardPreferences || hasCostCenterCardEditPreferences) && (
          <StyledTabPanel
            value={TabType.PREFERENCES}
            activePage={subPage}
            title={t('tabs.preferences')}
          >
            {planUnitId && (
              <Preferences
                costCenterId={planUnitId}
                referenceDate={date}
              ></Preferences>
            )}
          </StyledTabPanel>
        )}

        <StyledTabPanel
          value={TabType.SETTINGS}
          activePage={subPage}
          title={t('tabs.settings')}
        >
          {planUnitId && <Settings costCenterId={planUnitId}></Settings>}
        </StyledTabPanel>

        {hasCostCenterCardEditPreferences && (
          <StyledTabPanel
            value={TabType.FORMATION_AND_SORT_ORDER}
            activePage={subPage}
            title={t('tabs.formationAndSortOrder')}
          >
            {planUnitId && (
              <FormationAndSortorder
                costCenterId={planUnitId}
                date={date}
              ></FormationAndSortorder>
            )}
          </StyledTabPanel>
        )}

        {hasClustersOrgGrant && (
          <StyledTabPanel
            value={TabType.CLUSTERS}
            activePage={subPage}
            title={t('tabs.clusters')}
          >
            {planUnitId && <Clusters brId={planUnitId}></Clusters>}
          </StyledTabPanel>
        )}

        {showCostCalculationsTab && (
          <StyledTabPanel
            value={TabType.COST_CALCULATION_SETTINGS}
            activePage={subPage}
            title={t('tabs.costCalculationSettings')}
          >
            {planUnitId && (
              <CostCalculationExternalSettings
                organizationalUnitId={planUnitId}
              ></CostCalculationExternalSettings>
            )}
          </StyledTabPanel>
        )}
      </StyledBottomBox>
    </StyledPlanUnitsHomeBox>
  );
};

export default PlanUnitsHome;
